/**
 * stem-split-paid.js — the paid Demucs lane of /stem-splitter (self-hosted Demucs
 * on RunPod): info → submit → poll → result.
 *
 * Rebuilt 2026-08-17. What changed and why:
 *   - the example moved behind a TAB, so the default view is «put your file here»
 *     rather than five players of somebody else's track;
 *   - the hand-rolled drop zone became <x-tool-uploader> (adaptive size gate,
 *     FILE_LOST priming, collapse-to-file-row, keyboard) — the shared primitive
 *     this tool was skipping;
 *   - the result gained an AUDITION player: all stems on one clock with solo/mute,
 *     because «did the separation work» cannot be answered by four independent
 *     <audio> tags;
 *   - downloads gained a format (FLAC as separated / WAV / MP3);
 *   - the wait gained real stage names + a wake lock, this being a job measured in
 *     tens of seconds.
 *
 * Deliberately NOT added: a percentage or an ETA. The poll reports a job STATE,
 * not progress, and a bar interpolated from elapsed time would be invented — the
 * same trap `reference_long_run_needs_three_primitives` records for /audio-enhancer.
 *
 * Config + strings come from window.__stemSplit (rendered by
 * resources/views/tools/partials/stem-split-core.blade.php). Reuses the global
 * primitives: window.__downloadFile / __toast / __wakeLock / __bindUploaderFile.
 */
(function () {
  'use strict';
  var cfg = window.__stemSplit;
  if (!cfg) return;
  var T = cfg.strings || {};
  var els = {};
  var selectedFile = null;
  var lastStems = [];
  var pendingDup = null;   // the 409 payload while its panel is on screen

  function $(id) { return document.getElementById(id); }
  function toast(m, t) { if (window.__toast) window.__toast(m, t); }
  // «Sign in to split» must open the door, not only name it: navigate to the
  // login page with a safe internal ?redirect= back here (as /voice-cover does).
  //
  // Two things this door lacked, both measured on RU in the 30 days to 2026-09-18:
  //   • it was INVISIBLE — the press was refused before track('submit'), so 552
  //     guest sessions with a file in hand (475 of them sent to the login page,
  //     none ever split) left no event at all;
  //   • it DROPPED THE FILE — the page you come back to is a fresh one, and the
  //     reporter of 2026-09-18 chose the same 47 MB WAV four times in three minutes.
  // The file now rides the shared hand-off (IndexedDB, single-use, 30 min, ≤200 MB)
  // and is put back into the uploader on return. The door never waits for a slow
  // disk: 2.5 s, then it opens regardless.
  var PENDING_KEY = '__stem_splitter_pending_file_v1';
  function goLogin(which) {
    var f = selectedFile;
    track('guest_wall', { which: which || 'split', mb: f ? Math.round(f.size / 1048576) : 0 });
    if (!cfg.loginUrl) return;
    var left = false, t0 = Date.now();
    var leave = function (kept) {
      if (left) return;
      left = true;
      track('guest_wall_file', { kept: kept ? 1 : 0, ms: Date.now() - t0 });
      // the toast gets its 0.7 s either way
      setTimeout(function () { window.location.href = cfg.loginUrl; }, Math.max(0, 700 - (Date.now() - t0)));
    };
    if (f && typeof window.__toolHandoffPut === 'function') {
      window.__toolHandoffPut(PENDING_KEY, f).then(leave, function () { leave(false); });
      setTimeout(function () { leave(false); }, 2500);
    } else { leave(false); }
  }
  function restorePendingFile() {
    if (typeof window.__toolHandoffTake !== 'function' || !els.drop) return;
    window.__toolHandoffTake(PENDING_KEY).then(function (f) {
      if (!f || selectedFile) return;
      // the uploader wires itself on its own DOMContentLoaded; give it a moment
      var tries = 0;
      (function put() {
        if (selectedFile) return;
        if (typeof els.drop.__uploaderInjectFile === 'function') {
          els.drop.__uploaderInjectFile(f, 'login_return');
          track('pending_file_restored', { mb: Math.round(f.size / 1048576) });
          return;
        }
        if (++tries < 40) setTimeout(put, 100);
      })();
    }, function () {});
  }
  function track(action, extra) {
    try {
      var p = { slug: 'stem-splitter', action: action };
      if (extra) for (var k in extra) if (Object.prototype.hasOwnProperty.call(extra, k)) p[k] = extra[k];
      if (window.__ae) window.__ae('feature_use', p);
    } catch (_) {}
  }

  function init() {
    els.panel = $('stemPaid');
    if (!els.panel) return;
    els.drop = $('stemDrop');
    els.go = $('stemGo');
    els.cost = $('stemCost');
    els.balance = $('stemBalance');
    els.progress = $('stemProgress');
    els.stage = $('stemStage');
    els.eta = $('stemEta');
    els.result = $('stemResult');
    els.stems = $('stemStems');
    els.zip = $('stemZip');
    els.fmt = $('stemFmt');
    els.toMixer = $('stemToMixer');
    els.toMixerHint = $('stemToMixerHint');
    els.mixes = $('stemMixes');
    els.player = $('ssPlayer');
    els.play = $('ssPlay');
    els.seek = $('ssSeek');
    els.time = $('ssTime');
    els.previewGo = $('stemPreviewGo');
    els.previewBox = $('stemPreviewBox');
    els.previewStems = $('stemPreviewStems');
    els.previewLeft = $('stemPreviewLeft');
    els.modeWarn = $('stemModeWarn');
    els.fail = $('stemFail');
    els.failMsg = $('stemFailMsg');
    els.failRetry = $('stemFailRetry');
    els.note = $('stemNote');
    els.dup = $('stemDup');
    els.dupMsg = $('stemDupMsg');
    els.dupOpen = $('stemDupOpen');
    els.dupForce = $('stemDupForce');
    els.library = $('stemLibrary');
    els.restored = $('stemRestored');
    els.stageLib = $('stemStageLib');

    bindTabs();
    bindUpload();
    bindPlayer();
    bindModes();
    if (els.previewGo) els.previewGo.addEventListener('click', submitPreview);
    if (els.mixes) els.mixes.addEventListener('click', function (e) { var b = e.target.closest('.stem-mix-btn'); if (b) buildMix(b.dataset.mix, b); });
    if (els.go) els.go.addEventListener('click', submit);
    if (els.failRetry) els.failRetry.addEventListener('click', function () { hideFailure(); submit(); });
    if (els.dupOpen) els.dupOpen.addEventListener('click', acceptDuplicate);
    if (els.dupForce) els.dupForce.addEventListener('click', forceDuplicate);
    if (els.zip) els.zip.addEventListener('click', downloadZip);
    if (els.toMixer) els.toMixer.addEventListener('click', openInMixer);
    // Info first, then the memo: whether a remembered job may be asked about
    // depends on who is signed in (the status route answers 404 to anyone else).
    loadInfo().then(restoreFromMemo);
    restorePendingFile();
  }

  // ── Tabs: «own file» (default) / «example» ─────────────────────────────
  // The example is a second door, not the front one. Full ARIA tab semantics
  // including arrow-key roving focus — this is the page's primary navigation.
  function bindTabs() {
    var tabs = [$('ssTabOwn'), $('ssTabDemo')].filter(Boolean);
    if (tabs.length < 2) return;
    var panels = [$('ssPanelOwn'), $('ssPanelDemo')];

    function select(i, focus) {
      tabs.forEach(function (t, n) {
        var on = n === i;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        if (panels[n]) panels[n].hidden = !on;
      });
      if (focus) tabs[i].focus();
      // Leaving the tab must not leave audio playing behind it.
      if (i !== 0) pause();
      if (i !== 1) stopDemoAudio();
      track('tab', { tab: i === 0 ? 'own' : 'demo' });
    }

    tabs.forEach(function (t, i) {
      t.addEventListener('click', function () { select(i); });
      t.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); select((i + 1) % tabs.length, true); }
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); select((i - 1 + tabs.length) % tabs.length, true); }
        else if (e.key === 'Home') { e.preventDefault(); select(0, true); }
        else if (e.key === 'End') { e.preventDefault(); select(tabs.length - 1, true); }
      });
    });

    // «Split my own track» at the bottom of the example — the demo's whole job is
    // to send people back to the uploader, so it ends with the way there.
    var cta = $('ssDemoCta');
    if (cta) cta.addEventListener('click', function () { select(0); if (els.drop) els.drop.scrollIntoView({ block: 'nearest' }); });
  }

  function stopDemoAudio() {
    var p = $('ssPanelDemo');
    if (p) p.querySelectorAll('audio').forEach(function (a) { a.pause(); });
  }

  // ── Upload via the shared uploader ─────────────────────────────────────
  function bindUpload() {
    if (!els.drop) return;
    var onFile = function (f) { if (f) setFile(f); };
    // Never addEventListener('tool-uploader:file') directly: a file chosen BEFORE
    // the listener is attached would be dropped (UploaderListenerRaceTest).
    // ⚠️ The helper hands the callback the FILE itself — callback(file, detail) —
    // while the raw event carries it on e.detail.file. Treating the first argument
    // as an event is a silent no-op: the zone collapses to «file picked» and the
    // tool's own button stays dead (caught in the e2e, 2026-08-17).
    if (typeof window.__bindUploaderFile === 'function') {
      window.__bindUploaderFile(els.drop, function (f) { onFile(f); });
    } else {
      els.drop.addEventListener('tool-uploader:file', function (e) { onFile(e && e.detail && e.detail.file); });
    }
    els.drop.addEventListener('tool-uploader:clear', function () {
      selectedFile = null;
      if (els.go) els.go.disabled = true;
      resetResult();
    });
  }

  function setFile(f) {
    selectedFile = f;
    if (els.go) els.go.disabled = false;
    if (els.previewGo) els.previewGo.disabled = false;
    resetResult();
    quoteFor(f);
  }

  /*
   * Цена зависит от ДЛИТЕЛЬНОСТИ дорожки: время GPU идёт за ней, и плоская цена
   * означала убыток на длинных треках (51 живое списание, разбор — в
   * config/paid-tools.php). Пока файла нет, показан ПОЛ — цена самой короткой
   * дорожки.
   *
   * ⚠️ Считаем ТОЙ ЖЕ формулой и с ТЕМ ЖЕ округлением вверх, что и сервер
   * (StemSplitManager::cost): два разных счёта — это способ показать одно, а
   * списать другое. Числа приходят с сервера (`price`), в браузере их нет.
   *
   * ⚠️ Не сумели узнать длительность (браузер не открыл метаданные) — цену не
   * трогаем и оставляем пол: соврать в меньшую сторону хуже, чем промолчать,
   * потому что списание всё равно посчитает по настоящей длине.
   */
  function quoteFor(f) {
    var p = window.__stemInfo && window.__stemInfo.price;
    if (!p || !els.cost || !f) return;
    var url = URL.createObjectURL(f);
    var a = new Audio();
    var done = false;
    var finish = function (sec) {
      if (done) return;
      done = true;
      URL.revokeObjectURL(url);
      if (!(sec > 0)) return;
      var mins = Math.ceil(sec / 60);
      els.cost.textContent = Math.max(p.floor, p.base + p.per_min * mins);
    };
    a.addEventListener('loadedmetadata', function () { finish(a.duration); });
    a.addEventListener('error', function () { finish(0); });
    // Метаданные иногда не приезжают вовсе (кодек, который тег <audio> не знает);
    // тогда просто остаёмся на полу, а не ждём вечно.
    setTimeout(function () { finish(0); }, 8000);
    a.preload = 'metadata';
    a.src = url;
  }

  // ── Mode: 4 stems or 6 ─────────────────────────────────────────────────
  // The 6-stem warning is shown WITH the choice, not after the charge: on a song
  // without a guitar or a piano those two stems come back silent, and that is the
  // reason the model stayed unexposed until the job started measuring them.
  function bindModes() {
    var radios = document.querySelectorAll('input[name=stemMode]');
    if (!radios.length) return;
    var sync = function () {
      if (els.modeWarn) els.modeWarn.hidden = currentMode() !== 'htdemucs_6s';
    };
    radios.forEach(function (r) {
      r.addEventListener('change', function () { sync(); resetResult(); track('mode', { mode: currentMode() }); });
    });
    sync();
  }

  function currentMode() {
    var r = document.querySelector('input[name=stemMode]:checked');
    return r ? r.value : '';
  }

  function resetResult() {
    hideFailure();
    hideNote();
    hideDup();
    pause();
    releaseStems();
    lastStems = [];
    if (els.result) els.result.hidden = true;
    if (els.restored) els.restored.hidden = true;
    if (els.stems) els.stems.innerHTML = '';
    if (els.player) els.player.hidden = true;
    // A preview belongs to the file it was made from: keeping it visible after the
    // file changes would let someone judge track B by track A's excerpt.
    if (els.previewBox) els.previewBox.hidden = true;
    if (els.previewStems) els.previewStems.innerHTML = '';
  }

  function loadInfo() {
    return fetch(cfg.infoUrl, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (!d || !d.enabled) return;
        window.__stemInfo = d;
        if (els.cost) els.cost.textContent = d.cost;
        renderBalance(d.balance);
        renderPreviewLeft(d.preview_remaining);
        renderFreeLane();
        syncLibraryLink(d);
      })
      .catch(function () {});
  }

  function renderBalance(b) { if (els.balance && b != null) els.balance.textContent = b; }

  // The library link is for the person whose library it is; a guest sees the
  // login door on «Разделить» long before the result panel exists.
  function syncLibraryLink(d) {
    if (els.library) els.library.hidden = !!(d && d.login_required);
    // …and a guest is told so BEFORE choosing a file, with the two account-free tools named.
    var g = $('stemGuest');
    if (g) { var show = !!(d && d.login_required); if (show && g.hidden) track('guest_note_shown'); g.hidden = !show; }
  }

  // ── The job outlives the tab ───────────────────────────────────────────
  // Measured on prod (jobs 29882 → 29883, 2026-09-01): a Premium user on an
  // iPhone submitted a 42 MB WAV, the page was hidden/reloaded during the ~60 s
  // wait, and 35 minutes later he paid 1900 tokens AGAIN for the byte-identical
  // file — because the job id lived only inside submit()'s promise chain, and
  // init() had nothing to come back to, although the server keeps the stems
  // for a week and /my-generations lists them. So the paid job is written down
  // through the sanctioned settings primitive (never raw localStorage), and
  // init() asks the server about it before anything else.
  //
  // Cleared ONLY when: the next paid submit overwrites it; the server says the
  // job is gone (404/410/success:false/expired); the memo is older than the
  // retention window; or the job ended in error. NEVER at `done` — that is the
  // /video-upscaler lesson (user 5919 lost a paid result when the key was
  // cleared on completion) — and never on «Другой файл»: choosing a new file
  // is not a statement about the old result.
  var MEMO_TOOL = 'stem-splitter', MEMO_KEY = 'paid_job';
  function memoRead() {
    var m = window.__toolSettings ? window.__toolSettings.load(MEMO_TOOL, MEMO_KEY, null) : null;
    return (m && m.v === 1 && m.statusUrl) ? m : null;
  }
  function memoWrite(m) { if (window.__toolSettings) window.__toolSettings.save(MEMO_TOOL, MEMO_KEY, m); }
  function memoClear() { memoWrite(null); }
  function memoAgeMin(m) { return Math.max(0, Math.round((Date.now() - (+(m && m.at) || Date.now())) / 60000)); }

  // Placeholders by NAME through a function replacer: a string `.replace`
  // reads `$&` / `$1` inside a file name as a pattern, and a chain of
  // replaceAll(':' + name) lets a short name eat the head of a longer one
  // (reference_naive_placeholder_replacer_eats_the_next_name_2026_09_02).
  function fmt(s, map) {
    // ⚠️ Список плейсхолдеров ЗАКРЫТЫЙ, и это ловушка: строка со своим именем
    // (`:left`, `:cap`) молча уезжает на экран СЫРОЙ. Ровно так и вышло со
    // строкой бесплатного лейна 12.09.2026 — на экране стояло «Осталось
    // сегодня: :left из :cap», и выглядело это как непереведённая фраза, а не
    // как пропуск в регулярке.
    return String(s || '').replace(/:(name|ago|until|days|left|cap)\b/g, function (_, k) {
      return map && map[k] != null ? String(map[k]) : '';
    });
  }

  // «35 minutes ago» in the page's language without nine translations of the
  // unit: Intl.RelativeTimeFormat is in every engine that runs this lane.
  // Fallback is the absolute clock time — never an English word.
  function fmtAgo(ms) {
    var min = Math.max(1, Math.round(ms / 60000));
    try {
      var rtf = new Intl.RelativeTimeFormat(window.__LOCALE || 'en', { numeric: 'always' });
      if (min < 60) return rtf.format(-min, 'minute');
      if (min < 48 * 60) return rtf.format(-Math.round(min / 60), 'hour');
      return rtf.format(-Math.round(min / 1440), 'day');
    } catch (_) {
      return new Date(Date.now() - ms).toLocaleString(window.__LOCALE || 'en');
    }
  }
  function fmtUntil(iso) {
    var d = new Date(iso || '');
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString(window.__LOCALE || 'en');
  }

  function restoreFromMemo() {
    var m = memoRead();
    if (!m) return;
    var info = window.__stemInfo;
    // No info = the lane is dark or the request failed; keep the memo for a
    // visit that can actually ask.
    if (!info || !info.enabled) return;
    var age = memoAgeMin(m);
    var ttlMs = Math.max(1, +cfg.retentionDays || 7) * 86400000;
    var expired = (Date.now() - (+m.at || 0)) > ttlMs
      || (m.expiresAt && Date.parse(m.expiresAt) < Date.now());
    if (expired) { memoClear(); track('restore_gone', { reason: 'ttl', age_min: age }); return; }
    if (info.login_required) { track('restore_skipped_login', { age_min: age }); return; }

    hideFailure(); hideNote(); hideDup();
    setBusy(true);
    setStage(m.doneAt ? 'restoring' : 'processing', m);
    track(m.doneAt ? 'restore_done_load' : 'restore_resume', { age_min: age });
    poll(m.statusUrl, false, { restored: true, memo: m });
  }

  function showNote(msg) {
    if (!els.note) { toast(msg, 'info'); return; }
    els.note.textContent = msg;
    els.note.hidden = false;
    if (window.__announce) window.__announce(msg);
  }
  function hideNote() { if (els.note) els.note.hidden = true; }

  // «Осталось сегодня: N» — without it the button promises a free excerpt the
  // quota may already have spent, and the refusal arrives as a surprise.
  function renderPreviewLeft(n) {
    if (!els.previewLeft) return;
    if (n == null) { els.previewLeft.hidden = true; return; }
    els.previewLeft.textContent = (T.preview_left || '').replace(':n', n);
    els.previewLeft.hidden = false;
    if (els.previewGo && n <= 0) els.previewGo.disabled = true;
  }


  // ── The bytes, not the handle ──────────────────────────────────────────
  // A paid submit must not depend on the OS file handle still being alive.
  // Measured on prod (bug #5186, 2026-08-17): the first split failed, the user
  // pressed «Разделить» again 17 minutes later, and the second request NEVER
  // REACHED THE SERVER — Chrome refuses to send a multipart body whose File
  // changed on disk since the picker (the reporter came straight from
  // /music-mastering, i.e. was re-exporting that very master). The tool showed
  // the generic «Не удалось разделить трек» and looked broken, while nothing
  // had been asked of us at all.
  //
  // FileBytes already holds these bytes: the shared uploader primes ≤100 MB
  // into RAM and spills the rest to OPFS at pick time, precisely for handle
  // death. Resolving through it turns a dead handle from a silent network
  // failure into either a working upload or an honest «выберите файл снова».
  function resolveUpload() {
    var f = selectedFile;
    if (!f) return Promise.reject(new Error('no_file'));
    if (!window.FileBytes || typeof window.FileBytes.asFile !== 'function') return Promise.resolve(f);
    return window.FileBytes.asFile(f, f.name).catch(function (e) {
      // asFile already tried RAM, then the OPFS spill, then a live read of the
      // handle. If all three came back empty the bytes are genuinely gone, and
      // handing the dead handle to fetch anyway only converts a nameable problem
      // into «Failed to fetch» — which is what the user saw as «не разделяет».
      throw new Error('FILE_LOST: ' + (e && e.message ? e.message : e));
    });
  }

  function isFileLost(err) {
    return !!err && /FILE_LOST|NotFoundError|NotReadableError|no_file/i.test(String(err.message || err));
  }
  // The click handler receives an Event; `force` must be an explicit decision
  // (the duplicate panel's second button), never something an event object
  // happens to be truthy for.
  /* ── Тянет ли браузер нейросетевой движок ────────────────────────────────
     Причина, по которой НЕ тянет, или null. Спрашиваем ТОТ ЖЕ вердикт, что
     решает режим по умолчанию на /vocal-remover (MdxStemSeparator), а не свою
     проверку: две копии этого правила неминуемо разъедутся, и тогда одна
     страница будет считать устройство годным, а другая — выдавать ему
     серверный прогон «потому что оно негодное».
     Модуль подключён с defer и может опоздать: до его появления причина
     остаётся null, то есть прогон идёт обычным платным путём. Ошибаемся в
     сторону «не раздавать», а не в сторону «раздать не тем». */
  // ⚠️ This is the verdict of the KIM engine (/vocal-remover's «Ультра»), and since 2026-09-19 it
  // is no longer the only engine on the page: the free lane above runs MUSION, which needs WebGPU
  // and answers its own support(). Two engines, two questions — not a duplicated rule. The name
  // says which one this is, because the free-fallback line below depends on BOTH (see renderFreeLane).
  var ultraUnavailableReason = null;
  (function resolveUltraEngine() {
    var tries = 0;
    (function wait() {
      var M = window.MdxStemSeparator;
      if (!M || typeof M.defaultEngineVerdict !== 'function') {
        if (++tries > 100) return;            // ~10 c — сдаёмся, остаёмся платными
        return setTimeout(wait, 100);
      }
      M.defaultEngineVerdict().then(function (v) {
        if (v && !v.ultra && v.reason) {
          ultraUnavailableReason = v.reason;
          track('browser_engine_unavailable', { reason: v.reason, rate: v.rate || 0 });
          renderFreeLane();
        }
      }, function () {});
    })();
  })();

  /* Строка «этот прогон не спишет токены» появляется ТОЛЬКО когда сервер
     действительно готов его так провести. Число остатка приходит с сервера
     (free_left) — своего счётчика здесь нет, он разошёлся бы с настоящим. */
  function renderFreeLane() {
    var el = document.getElementById('stemFreeLane');
    if (!el) return;
    var info = window.__stemInfo || {};
    // Say «this run will not spend tokens» only when the person really has no browser road at
    // all. With the free MUSION lane on the page, keying this on the kim verdict alone would put
    // «browser separation is unavailable on your device» on screen right under a live button.
    var laneOpen = window.__stemBrowserLane ? window.__stemBrowserLane.canRun() : false;
    if (!ultraUnavailableReason || laneOpen || !info.free_fallback_cap) { el.hidden = true; return; }
    var left = (typeof info.free_fallback_left === 'number') ? info.free_fallback_left : info.free_fallback_cap;
    var t = fmt(T.free_lane || '', { left: left, cap: info.free_fallback_cap });
    el.textContent = t;
    el.hidden = !t;
  }

  function submit() { doSubmit(false); }

  function doSubmit(force) {
    if (!selectedFile) { toast(T.pick_file || 'Choose a file', 'warning'); return; }
    var info = window.__stemInfo || {};
    if (info.login_required) { toast(T.login || 'Please sign in', 'warning'); goLogin('split'); return; }

    hideFailure(); hideNote(); hideDup();
    setBusy(true);
    setStage('queued');
    track('submit', { mode: currentMode(), force: force ? 1 : 0 });
    var sent = null;
    resolveUpload()
      .then(function (file) {
        sent = file;
        var fd = new FormData();
        fd.append('source', file);
        if (currentMode()) fd.append('mode', currentMode());
        if (force) fd.append('force', '1');
        // Бесплатный фолбэк: просим его ВСЕГДА, когда браузерный движок здесь
        // не работает. Сервер сам решает, положен ли он (тир + свободный слот),
        // и при отказе молча уходит на обычную платную ветку — поэтому просьба
        // ничего не ломает и не требует второго нажатия.
        if (ultraUnavailableReason) {
          fd.append('fallback', '1');
          fd.append('fallback_reason', ultraUnavailableReason);
          // Цена на этой странице показана ДО нажатия, поэтому, если бесплатный
          // слот кончился, прогон по цене — решение человека. Без этого поля
          // сервер с 23.09 отвечает отказом (его шлёт /vocal-remover сам, без цены).
          fd.append('accept_paid', '1');
        }
        return fetch(cfg.runUrl, {
          method: 'POST', body: fd, credentials: 'same-origin',
          headers: { Accept: 'application/json', 'X-CSRF-TOKEN': cfg.csrf },
        });
      })
      .then(function (r) { return r.json().then(function (j) { return { status: r.status, d: j }; }); })
      .then(function (res) {
        if (res.status === 202 && res.d.status_url) {
          // Written on the 202 and nowhere earlier: this is the moment a job
          // exists on the server to come back to. (submitPreview writes nothing
          // — an excerpt is not a purchase and is reaped in hours.)
          var m = {
            v: 1, jobId: res.d.job_id || null, statusUrl: res.d.status_url,
            name: (sent && sent.name) || selectedFile.name, bytes: (sent && sent.size) || selectedFile.size,
            mode: res.d.mode || currentMode(), cost: res.d.cost || null,
            free: !!res.d.free,
            at: Date.now(), doneAt: null, expiresAt: null,
          };
          memoWrite(m);
          track('submit_accepted', { free: res.d.free ? 1 : 0, free_left: res.d.free_left, cost: res.d.cost || 0 });
          poll(res.d.status_url, false, { memo: m });
          return;
        }
        // Тот же файл уже в работе (очередь, перезагрузка, второе устройство) —
        // присоединяемся к нему: второго задания и второй цены не существует.
        if (res.status === 409 && res.d && res.d.in_flight && res.d.status_url) {
          var mf = {
            v: 1, jobId: res.d.job_id || null, statusUrl: res.d.status_url,
            name: res.d.original_name || (sent && sent.name) || selectedFile.name,
            bytes: (sent && sent.size) || selectedFile.size,
            mode: res.d.mode || currentMode(), cost: null, free: false,
            at: Date.parse(res.d.queued_at || '') || Date.now(), doneAt: null, expiresAt: null,
          };
          memoWrite(mf);
          track('submit_joined_in_flight', { job: res.d.job_id || 0 });
          poll(res.d.status_url, false, { memo: mf });
          return;
        }
        setBusy(false);
        if (res.status === 409 && res.d && res.d.error_kind === 'duplicate') { offerDuplicate(res.d); return; }
        handleError(res.d);
      })
      .catch(function (err) { setBusy(false); failedToSend(err); });
  }

  // ── «You already have this» ────────────────────────────────────────────
  // The server refused to sell the same separation twice (409 duplicate: same
  // file, same mode, result still on disk). Nothing was reserved or held. The
  // existing stems are one click away; paying again is the other button and
  // sends force=1 — an explicit decision, not a retry.
  function offerDuplicate(d) {
    pendingDup = d;
    var doneAt = Date.parse(d.done_at || '') || Date.now();
    track('dup_offered', { age_min: Math.max(0, Math.round((Date.now() - doneAt) / 60000)) });
    var msg = fmt(T.dup_notice, { name: d.original_name || (selectedFile && selectedFile.name) || '', ago: fmtAgo(Date.now() - doneAt) });
    if (!els.dup || !els.dupMsg) { showNote(msg); return; }
    els.dupMsg.textContent = msg;
    els.dup.hidden = false;
    if (window.__announce) window.__announce(msg);
  }

  function hideDup() { if (els.dup) els.dup.hidden = true; pendingDup = null; }

  function acceptDuplicate() {
    var d = pendingDup;
    if (!d || !d.status_url) return;
    hideDup();
    track('dup_accepted');
    var when = Date.parse(d.done_at || '') || Date.now();
    // The existing job becomes THE remembered job: a reload after this click
    // restores it exactly like a job this page had started itself.
    var m = {
      v: 1, jobId: d.job_id || null, statusUrl: d.status_url,
      name: d.original_name || (selectedFile && selectedFile.name) || '', bytes: selectedFile ? selectedFile.size : null,
      mode: d.mode || currentMode(), cost: null,
      at: when, doneAt: when, expiresAt: d.expires_at || null,
    };
    memoWrite(m);
    hideFailure(); hideNote();
    setBusy(true);
    setStage('restoring', m);
    poll(d.status_url, false, { restored: true, memo: m, via: 'dup' });
  }

  function forceDuplicate() {
    if (!pendingDup) return;
    hideDup();
    track('dup_forced');
    doSubmit(true);
  }

  // A request that never left the browser is not «separation failed» — saying so
  // sends the user looking for a fault on our side when the fix is one click of
  // «Другой файл».
  function failedToSend(err) {
    track('send_failed', { lost: isFileLost(err) ? 1 : 0 });
    var m = (isFileLost(err) ? T.file_lost : T.error) || T.error || 'Error';
    toast(m, 'error');
    showFailure(m);
  }

  // ── The failure that stays ─────────────────────────────────────────────
  // A toast is the wrong surface for the end of a minutes-long paid job: it
  // fades, and a user who switched tabs while «мы предупредили, что это займёт
  // время» never sees it. Whatever else we say, the panel keeps saying it, with
  // the way forward attached.
  function showFailure(msg) {
    if (!els.fail || !els.failMsg) return;
    els.failMsg.textContent = msg;
    els.fail.hidden = false;
    if (window.__announce) window.__announce(msg);
  }

  function hideFailure() { if (els.fail) els.fail.hidden = true; }

  function handleError(j) {
    var k = j && j.error_kind;
    track('error', { kind: k || 'unknown' });
    if (k === 'insufficient') {
      toast(T.insufficient || 'Not enough tokens', 'warning');
      if (j.can_purchase && j.store_url) {
        setTimeout(function () { if (confirm(T.topup || 'Top up your balance?')) window.location.href = j.store_url; }, 150);
      }
    } else if (k === 'login_required') {
      toast(T.login || 'Please sign in', 'warning'); goLogin('server_401');
    } else if (k === 'gpu_unavailable' || k === 'capacity') {
      toast(T.busy || 'Service is busy, try again shortly', 'warning');
      showFailure(T.busy || 'Service is busy, try again shortly');
    } else if (k === 'split_failed') {
      // the run died on OUR side (worker crash after the built-in retry) —
      // name it, say the tokens are safe, invite one more try
      var m = T.split_failed || T.busy || 'Separation failed on our side — tokens were not charged, please try again';
      toast(m, 'warning');
      showFailure(m);
    } else if (k === 'disabled') {
      toast(T.disabled || 'Unavailable', 'warning');
    } else if (k === 'preview_daily_cap') {
      toast(T.preview_daily_cap || 'No free excerpts left today', 'warning');
      renderPreviewLeft(0);
    } else if (k === 'source_too_short' || k === 'source_too_long' || k === 'source_not_audio') {
      toast(T[k] || T.bad_audio || 'This file cannot be processed', 'warning');
    } else {
      toast((j && j.error_msg) || T.error || 'Separation failed', 'error');
    }
  }

  // Stage text comes from the job's REAL state. No percentage is shown: the poll
  // reports a state, and a bar interpolated from elapsed time would be a guess
  // dressed as a measurement.
  //
  // One honest escalation: past 90 s the wait is almost certainly a cold GPU
  // start (worker boot ~100 s measured; a fresh host pulling the 4.9 GB image
  // is 7–10 min), so SAY that — with the one fact that makes a long wait
  // survivable: tokens are only charged for a delivered result. Silence here is
  // what «грузит и не открывает» complaints are made of.
  var stageTimer = null;
  function setStage(s, memo) {
    if (!els.stage) return;
    // 'restoring' = a FINISHED job being fetched back after a reload / on a
    // later visit: the wait is a download, not a GPU, and the line names the
    // file so the person knows which result is coming.
    var text = s === 'queued' ? T.stage_queued
      : s === 'restoring' ? fmt(T.restore_wait, { name: memo && memo.name })
      : T.stage_processing;
    els.stage.textContent = text || els.stage.textContent;
    if (els.eta) els.eta.textContent = T.keep_tab || '';
    if (stageTimer) { clearTimeout(stageTimer); stageTimer = null; }
    if (s === 'processing' && T.stage_long) {
      stageTimer = setTimeout(function () {
        if (els.stage) els.stage.textContent = T.stage_long;
      }, 90000);
    }
  }

  // ── Free 30-second excerpt ─────────────────────────────────────────────
  function submitPreview() {
    if (!selectedFile) { toast(T.pick_file || 'Choose a file', 'warning'); return; }
    var info = window.__stemInfo || {};
    if (info.login_required) { toast(T.login || 'Please sign in', 'warning'); goLogin('preview'); return; }

    hideFailure();
    setBusy(true, true);
    track('preview_submit', { mode: currentMode() });
    resolveUpload()
      .then(function (file) {
        var fd = new FormData();
        fd.append('source', file);
        if (currentMode()) fd.append('mode', currentMode());
        return fetch(cfg.previewUrl, {
          method: 'POST', body: fd, credentials: 'same-origin',
          headers: { Accept: 'application/json', 'X-CSRF-TOKEN': cfg.csrf },
        });
      })
      .then(function (r) { return r.json().then(function (j) { return { status: r.status, d: j }; }); })
      .then(function (res) {
        if (res.status === 202 && res.d.status_url) { poll(res.d.status_url, true); return; }
        setBusy(false);
        handleError(res.d);
      })
      .catch(function (err) { setBusy(false); failedToSend(err); });
  }

  function renderPreview(d) {
    if (!els.previewStems) return;
    els.previewStems.innerHTML = '';
    (d.stems || []).forEach(function (s) {
      var row = document.createElement('div');
      row.className = 'stem-prev-row';

      var label = document.createElement('span');
      label.className = 'stem-label';
      label.textContent = stemLabel(s.stem);
      if (s.quiet) label.appendChild(quietBadge());

      var a = document.createElement('audio');
      a.controls = true; a.preload = 'none'; a.src = s.url;

      row.appendChild(label);
      row.appendChild(a);
      els.previewStems.appendChild(row);
    });
    if (els.previewBox) els.previewBox.hidden = false;
    track('preview_done', { stems: (d.stems || []).length });
    refreshInfo();
  }

  function stemLabel(name) { return T['stem_' + name] || name; }

  function quietBadge() {
    var b = document.createElement('span');
    b.className = 'stem-quiet-badge';
    b.textContent = T.quiet_badge || 'silent';
    b.title = T.quiet_hint || '';
    return b;
  }

  function poll(statusUrl, isPreview, opts) {
    opts = opts || {};
    // Ceiling comes from the SERVER's own deadline (cfg.pollMaxS), never a
    // literal: a page that stops asking before the job settles reports
    // «слишком долго» about a run that then succeeds and charges for it.
    var tries = 0, max = Math.ceil((cfg.pollMaxS || 1020) / 2.5), seenProcessing = false;
    (function tick() {
      fetch(statusUrl, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        // The HTTP status travels WITH the body: only done/error used to end
        // this loop, so a 404 (row reaped) or 410 (files reaped) — which is what
        // a job restored a week later answers — kept it asking for the whole
        // pollMaxS ceiling, ~17 minutes of spinner over a result that no longer
        // exists. A body that is not JSON (an HTML error page) is kept apart
        // from «the job is gone»: a transient 502 must not erase the memo.
        .then(function (r) {
          return r.json().then(function (j) { return { http: r.status, d: j || {} }; },
                               function () { return { http: r.status, d: null }; });
        })
        .then(function (res) {
          var d = res.d;
          if (res.http === 404 || res.http === 410 || (d && d.success === false)) { jobGone(opts, res.http, isPreview); return; }
          if (!d) throw new Error('bad_status_body');
          if (d.status === 'done') {
            if (!isPreview && d.expired === true) { jobGone(opts, 'expired', false); return; }
            setBusy(false);
            if (isPreview) { renderPreview(d); } else { onDone(d, opts); }
            return;
          }
          if (d.status === 'error') {
            setBusy(false);
            handleError(d);
            // The failure panel is on screen; there is nothing to come back for.
            if (!isPreview) memoClear();
            return;
          }
          if (!seenProcessing && d.status && d.status !== 'queued') { seenProcessing = true; setStage('processing'); }
          if (++tries < max) setTimeout(tick, 2500); else { setBusy(false); toast(T.timeout || 'Timed out', 'error'); showFailure(T.timeout || 'Timed out'); }
        })
        .catch(function () { if (++tries < max) setTimeout(tick, 3000); else setBusy(false); });
    })();
  }

  // The row is gone (404 — audio-jobs:cleanup), the files are gone (410 /
  // expired:true — media:purge-expired nulled the variants), or the server
  // disowned it (success:false). Restoring: a calm note, not the red failure
  // panel — the reaper doing its job a week later is not a fault to retry.
  function jobGone(opts, why, isPreview) {
    setBusy(false);
    if (isPreview) { toast(T.preview_none || T.error || 'Error', 'warning'); return; }
    var m = opts.memo || memoRead();
    memoClear();
    if (opts.restored) {
      track('restore_gone', { age_min: memoAgeMin(m), http: why });
      showNote(fmt(T.restore_gone, { name: m && m.name, days: cfg.retentionDays || 7 }));
    } else {
      track('error', { kind: 'gone', http: why });
      showFailure(T.error || 'Error');
    }
  }

  function onDone(d, opts) {
    renderResult(d, opts);
    var m = opts.memo || memoRead();
    // Stamp the delivery on the memo: the next visit restores a FINISHED job
    // (the line says «loading your stems», not «separating»), and the expiry
    // lets the page stop asking once the server has forgotten.
    if (m && (!d.job_id || !m.jobId || String(m.jobId) === String(d.job_id))) {
      m.doneAt = m.doneAt || Date.now();
      if (d.expires_at) m.expiresAt = d.expires_at;
      if (!m.name && d.original_name) m.name = d.original_name;
      memoWrite(m);
    }
    if (opts.restored) {
      track('restore_done', { age_min: memoAgeMin(m), mode: d.mode || '', via: opts.via || 'memo' });
    } else {
      refreshBalance();
      track('done');
    }
  }

  // ── Result ─────────────────────────────────────────────────────────────
  function renderResult(d, opts) {
    lastStems = d.stems || [];
    // What the separator actually produced (FLAC in production, WAV under the
    // mock provider). The «as separated» download option and the e2e both need
    // to know it rather than assume FLAC.
    window.__stemLastFormat = (lastStems[0] && lastStems[0].format) || 'flac';
    paintFormatChoice(window.__stemLastFormat);
    releaseStems();
    if (els.stems) els.stems.innerHTML = '';

    var ctx = audioCtx();
    lastStems.forEach(function (s) {
      var row = document.createElement('div');
      row.className = 'stem-row lg-card';

      if (s.quiet) row.classList.add('is-quiet');

      var label = document.createElement('span');
      label.className = 'stem-label';
      label.textContent = stemLabel(s.stem);
      // A stem measured as silence is still delivered — it was paid for and the
      // file exists — but it is NAMED, so nobody downloads emptiness expecting a
      // guitar. This is what makes the 6-stem mode safe to offer at all.
      if (s.quiet) label.appendChild(quietBadge());

      // Streaming element, not a decoded buffer: four decoded stems of a 10-minute
      // track are ~850 MB of Float32 and would take a phone down.
      var audio = document.createElement('audio');
      audio.preload = 'metadata';
      audio.src = s.url;
      audio.crossOrigin = 'use-credentials';
      audio.loop = false;

      var toggles = document.createElement('div');
      toggles.className = 'stem-row-toggles';
      var solo = mkToggle(T.solo || 'S', (T.solo_full || 'Solo') + ' — ' + label.textContent);
      var mute = mkToggle(T.mute || 'M', (T.mute_full || 'Mute') + ' — ' + label.textContent);
      toggles.appendChild(solo); toggles.appendChild(mute);

      var vol = document.createElement('input');
      vol.type = 'range'; vol.className = 'ss-vol';
      vol.min = '0'; vol.max = '100'; vol.value = '100';
      vol.setAttribute('aria-label', (T.volume_for || 'Volume') + ' — ' + label.textContent);

      var dl = document.createElement('button');
      dl.type = 'button';
      dl.className = 'lg-btn stem-dl';
      dl.textContent = T.download || 'Download';
      dl.addEventListener('click', function () { downloadStem(s, dl); });

      var entry = { stem: s.stem, audio: audio, solo: false, mute: false, vol: 1, gain: null, el: { solo: solo, mute: mute } };

      // MediaElementSource keeps streaming decode while giving us a gain node for
      // solo/mute. Guarded: Safari throws if the element is re-wired, and a failure
      // here must not cost the user the result — playback simply falls back to the
      // element's own volume.
      if (ctx) {
        try {
          var src = ctx.createMediaElementSource(audio);
          var g = ctx.createGain();
          src.connect(g); g.connect(ctx.destination);
          entry.gain = g;
        } catch (_) { entry.gain = null; }
      }

      solo.addEventListener('click', function () { entry.solo = !entry.solo; solo.classList.toggle('is-on', entry.solo); applyGains(); });
      mute.addEventListener('click', function () { entry.mute = !entry.mute; mute.classList.toggle('is-on', entry.mute); applyGains(); });
      vol.addEventListener('input', function () { entry.vol = (+vol.value) / 100; applyGains(); });

      audio.addEventListener('loadedmetadata', updateDuration);
      players.push(entry);

      row.appendChild(label);
      row.appendChild(toggles);
      row.appendChild(vol);
      row.appendChild(dl);
      var wave = waveSvg(s);
      if (wave) row.appendChild(wave);
      if (els.stems) els.stems.appendChild(row);
    });

    applyGains();
    // A restored result says what it is: which file, split when, kept until
    // when — the person may have arrived here days later from a different
    // device with no memory of which track this was.
    if (els.restored) {
      if (opts && opts.restored) {
        var m = opts.memo || {};
        var since = +m.doneAt || +m.at || Date.parse(d.created_at || '') || Date.now();
        els.restored.textContent = fmt(T.restored_note, {
          name: m.name || d.original_name || '',
          ago: fmtAgo(Date.now() - since),
          until: fmtUntil(d.expires_at || m.expiresAt),
        });
        els.restored.hidden = false;
      } else {
        els.restored.hidden = true;
      }
    }
    if (els.result) els.result.hidden = false;
    if (els.player) els.player.hidden = players.length < 2;
    if (els.zip) els.zip.hidden = lastStems.length < 2;
    if (els.toMixer) els.toMixer.hidden = lastStems.length < 2;
    // The hint travels with the button: it is what turns «a place» into «what
    // you can do there» (per-track EQ, compressor, pan).
    if (els.toMixerHint) els.toMixerHint.hidden = lastStems.length < 2;
    if (els.mixes) { els.mixes.hidden = lastStems.length < 4; decodedStems = {}; }
  }

  /**
   * Waveform from the bars the JOB measured (meta.stems[].wave, 0..100 each).
   * Drawn from server data on purpose: deriving it in the page would mean
   * decoding every stem, which is the ~850 MB that the streaming player exists
   * to avoid. Absent data → no strip, never a fake flat line.
   */
  /**
   * The FREE browser lane (public/js/stem-split-browser.js) hands its four stems in here.
   *
   * Why an export instead of its own result UI: everything below a finished split — the audition
   * player on one clock, per-stem download in three formats, the ZIP, the ready mixes and the
   * bridge that puts four faders in /audio-mixer — is this module's, and it is driven entirely by
   * {stem, url, format} plus the optional wave. A second copy of that for the free lane would be
   * the kind of divergence that gets found months later on one road only.
   *
   * The lane's stems are blob: URLs; fetch(), decodeAudioData and <audio> all take them, and the
   * lane itself owns revoking them. `local` marks the result as ours-in-this-tab: no library link
   * and no expiry note, because nothing of it lives on our disk.
   */
  window.__stemShowLocal = function (stems) {
    if (!Array.isArray(stems) || !stems.length) return;
    hideFailure(); hideNote(); hideDup();
    memoClear();                               // a local split is not a job anyone can restore
    if (els.library) els.library.hidden = true;
    renderResult({ stems: stems }, { local: true });
    if (els.result && els.result.scrollIntoView) {
      try { els.result.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch (_) {}
    }
  };

  function waveSvg(s) {
    var w = s && s.wave;
    if (!w || !w.length) return null;
    var NS = 'http://www.w3.org/2000/svg';
    var svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('class', 'stem-wave');
    svg.setAttribute('viewBox', '0 0 ' + w.length + ' 100');
    svg.setAttribute('preserveAspectRatio', 'none');
    svg.setAttribute('role', 'img');
    svg.setAttribute('aria-label', (T.wave_aria || '').replace(':stem', stemLabel(s.stem)));
    for (var i = 0; i < w.length; i++) {
      var h = Math.max(1, Math.min(100, +w[i] || 0));
      var r = document.createElementNS(NS, 'rect');
      r.setAttribute('x', String(i + 0.15));
      r.setAttribute('y', String((100 - h) / 2));
      r.setAttribute('width', '0.7');
      r.setAttribute('height', String(h));
      svg.appendChild(r);
    }
    return svg;
  }

  function mkToggle(text, aria) {
    var b = document.createElement('button');
    b.type = 'button'; b.className = 'ss-tog'; b.textContent = text;
    b.setAttribute('aria-label', aria);
    b.setAttribute('aria-pressed', 'false');
    b.addEventListener('click', function () { b.setAttribute('aria-pressed', b.classList.contains('is-on') ? 'true' : 'false'); });
    return b;
  }

  // ── Audition transport ─────────────────────────────────────────────────
  var players = [];
  var _ctx = null, playing = false, seeking = false, rafId = 0;

  function audioCtx() {
    if (_ctx) return _ctx;
    var AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    try { _ctx = new AC(); } catch (_) { _ctx = null; }
    return _ctx;
  }

  function releaseStems() {
    pause();
    players.forEach(function (p) { try { p.audio.pause(); p.audio.removeAttribute('src'); p.audio.load(); } catch (_) {} });
    players = [];
  }

  // Solo wins over mute, exactly as on a console: if anything is soloed, only the
  // soloed stems are heard.
  function applyGains() {
    var anySolo = players.some(function (p) { return p.solo; });
    players.forEach(function (p) {
      var audible = anySolo ? p.solo : !p.mute;
      var v = audible ? p.vol : 0;
      if (p.gain) p.gain.gain.value = v;
      else p.audio.volume = v;                    // fallback when WebAudio wiring failed
      p.el.mute.classList.toggle('is-off', anySolo && !p.solo);
    });
  }

  function bindPlayer() {
    if (els.play) els.play.addEventListener('click', function () { playing ? pause() : play(); });
    if (els.seek) {
      els.seek.addEventListener('input', function () { seeking = true; });
      els.seek.addEventListener('change', function () {
        var d = duration();
        if (d) seekTo(d * (+els.seek.value) / 1000);
        seeking = false;
      });
    }
    // A tab going away should not leave four streams playing.
    document.addEventListener('visibilitychange', function () { if (document.hidden) pause(); });
  }

  function duration() {
    var d = 0;
    players.forEach(function (p) { if (isFinite(p.audio.duration) && p.audio.duration > d) d = p.audio.duration; });
    return d;
  }

  function play() {
    if (!players.length) return;
    var ctx = audioCtx();
    // Safari/Chrome-on-Android refuse a context resumed outside a gesture; the
    // shared guard is the only sanctioned way to do it.
    if (ctx && ctx.state === 'suspended') {
      if (window.__safeResume) window.__safeResume(ctx);
      else { try { ctx.resume(); } catch (_) {} }
    }
    var at = players[0].audio.currentTime || 0;
    players.forEach(function (p) { p.audio.currentTime = at; });     // re-sync on every start
    Promise.all(players.map(function (p) { return p.audio.play().catch(function () {}); })).then(function () {
      playing = true;
      setPlayIcon();
      if (window.__wakeLock) window.__wakeLock.request();
      loop();
    });
    track('audition_play');
  }

  function pause() {
    if (!players.length) { playing = false; return; }
    players.forEach(function (p) { try { p.audio.pause(); } catch (_) {} });
    playing = false;
    setPlayIcon();
    if (rafId) { cancelAnimationFrame(rafId); rafId = 0; }
    if (window.__wakeLock && window.__wakeLock.release) window.__wakeLock.release();
  }

  function seekTo(t) {
    players.forEach(function (p) { try { p.audio.currentTime = t; } catch (_) {} });
    paint();
  }

  function setPlayIcon() {
    if (!els.play) return;
    var i = els.play.querySelector('.ss-play-icon');
    if (i) i.textContent = playing ? '❚❚' : '▶';
    els.play.setAttribute('aria-label', (playing ? T.player_pause : T.player_play) || '');
  }

  function loop() {
    if (!playing) return;
    paint();
    // Streams can drift apart over minutes; nudge any stem that wandered more
    // than 120 ms from the leader instead of letting the mix smear.
    var lead = players[0].audio.currentTime;
    players.forEach(function (p, i) {
      if (i && Math.abs(p.audio.currentTime - lead) > 0.12) { try { p.audio.currentTime = lead; } catch (_) {} }
    });
    if (players[0].audio.ended) { pause(); seekTo(0); return; }
    rafId = requestAnimationFrame(loop);
  }

  function paint() {
    var d = duration(), t = players.length ? players[0].audio.currentTime : 0;
    if (els.seek && !seeking) els.seek.value = d ? String(Math.round(t / d * 1000)) : '0';
    if (els.time) els.time.textContent = fmtTime(t) + (d ? ' / ' + fmtTime(d) : '');
  }

  function updateDuration() { paint(); }

  function fmtTime(s) {
    s = Math.max(0, Math.floor(s || 0));
    var m = Math.floor(s / 60);
    var r = s % 60;
    return m + ':' + (r < 10 ? '0' : '') + r;
  }

  // ── Downloads ──────────────────────────────────────────────────────────
  // FLAC is what the separator produced; WAV and MP3 are converted here so the
  // stems drop straight into an editor that will not read FLAC.
  function wantedFormat() { return (els.fmt && els.fmt.value) || 'flac'; }

  /* One owner for what the picker PROMISES. Its first option means «as separated», so its name
     follows the bytes: FLAC from the studio worker, WAV from the browser lane. And when the lane
     already produced WAV, the plain WAV option steps aside instead of offering the same file a
     second time through a pointless decode-and-re-encode. */
  function paintFormatChoice(srcFmt) {
    if (!els.fmt) return;
    var asIs = els.fmt.querySelector('option[value="flac"]');
    var wav = els.fmt.querySelector('option[value="wav"]');
    var srcIsWav = srcFmt === 'wav';
    if (asIs) asIs.textContent = (srcIsWav ? (T.fmt_wav_src || T.fmt_flac) : T.fmt_flac) || asIs.textContent;
    if (wav) {
      wav.hidden = srcIsWav;
      wav.disabled = srcIsWav;
      if (srcIsWav && els.fmt.value === 'wav') els.fmt.value = 'flac';
    }
  }

  function downloadStem(s, btn) {
    var fmt = wantedFormat();
    var name = (T['stem_' + s.stem] || s.stem);
    if (fmt === 'flac') {
      fetchBlob(s.url).then(function (bl) { save(bl, name + '.' + (s.format || 'flac')); })
        .catch(function () { toast(T.error || 'Error', 'error'); });
      track('download', { stem: s.stem, fmt: s.format || 'flac' });
      return;
    }
    var label = btn.textContent;
    btn.classList.add('is-busy'); btn.disabled = true; btn.textContent = T.converting || label;
    convert(s, fmt).then(function (out) { save(out.blob, name + '.' + out.ext); track('download', { stem: s.stem, fmt: fmt }); })
      .catch(function () { toast(T.error || 'Error', 'error'); })
      .then(function () { btn.classList.remove('is-busy'); btn.disabled = false; btn.textContent = label; });
  }

  function fetchBlob(url) {
    return fetch(url, { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('http ' + r.status);
      return r.blob();
    });
  }

  function convert(s, fmt) {
    return stemBuffer(s.stem).then(function (buf) {
      if (fmt === 'wav') return { blob: window.AudioPost.encodeWav(buf), ext: 'wav' };
      return { blob: encodeMp3(buf), ext: 'mp3' };
    });
  }

  // 192 kbps joint-stereo: a stem is raw material for further editing, so this is
  // the lowest rate that does not audibly pre-damage it.
  function encodeMp3(buf) {
    if (!window.lamejs) throw new Error('lamejs missing');
    var ch = Math.min(2, buf.numberOfChannels);
    var enc = new window.lamejs.Mp3Encoder(ch, buf.sampleRate, 192);
    var L = to16(buf.getChannelData(0));
    var R = ch > 1 ? to16(buf.getChannelData(1)) : null;
    var out = [], block = 1152;
    for (var i = 0; i < L.length; i += block) {
      var l = L.subarray(i, i + block);
      var r = R ? R.subarray(i, i + block) : null;
      var d = r ? enc.encodeBuffer(l, r) : enc.encodeBuffer(l);
      if (d.length) out.push(d);
    }
    var end = enc.flush();
    if (end.length) out.push(end);
    return new Blob(out, { type: 'audio/mpeg' });
  }

  function to16(f32) {
    var o = new Int16Array(f32.length);
    for (var i = 0; i < f32.length; i++) {
      var v = f32[i] < -1 ? -1 : f32[i] > 1 ? 1 : f32[i];
      o[i] = v < 0 ? v * 0x8000 : v * 0x7fff;
    }
    return o;
  }

  // ── Ready mixes: sums of the same stems, built in the browser ──────────
  //    Decoded stems are cached per result so four buttons do not fetch the
  //    FLACs four times. Decoding goes through an OfflineAudioContext at the
  //    stem's OWN sample rate (sniffed from the header): a live AudioContext
  //    decodes at the DEVICE rate, so a 44.1 kHz stem on a 48 kHz laptop would
  //    come back resampled — measured in the e2e (44100 in → 48000 out).
  var decodedStems = {};
  var MIX_DEF = { drums: ['vocals', 'bass', 'other'], vocals: ['drums', 'bass', 'other'], bass: ['vocals', 'drums', 'other'], rhythm: ['drums', 'bass'] };
  var MP3_RATES = [[11025, 12000, 8000], [0, 0, 0], [22050, 24000, 16000], [44100, 48000, 32000]];
  function sniffRate(ab) {
    var b = new Uint8Array(ab, 0, Math.min(ab.byteLength, 4096));
    if (b.length > 21 && b[0] === 0x66 && b[1] === 0x4c && b[2] === 0x61 && b[3] === 0x43) {           // fLaC → STREAMINFO
      return (b[18] << 12) | (b[19] << 4) | (b[20] >> 4);
    }
    if (b.length > 36 && b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46) {           // RIFF/WAVE → fmt
      for (var o = 12; o + 8 <= b.length;) {
        var len = b[o + 4] | (b[o + 5] << 8) | (b[o + 6] << 16) | (b[o + 7] << 24);
        if (b[o] === 0x66 && b[o + 1] === 0x6d && b[o + 2] === 0x74) return b[o + 12] | (b[o + 13] << 8) | (b[o + 14] << 16) | (b[o + 15] << 24);
        o += 8 + len + (len & 1);
      }
    }
    for (var i = 0; i + 2 < b.length; i++) {                                                          // MPEG frame sync
      if (b[i] === 0xff && (b[i + 1] & 0xe0) === 0xe0) { var r = MP3_RATES[(b[i + 1] >> 3) & 3][(b[i + 2] >> 2) & 3]; if (r) return r; }
    }
    return 0;
  }
  function stemBuffer(name) {
    if (decodedStems[name]) return decodedStems[name];
    var s = null; for (var i = 0; i < lastStems.length; i++) if (lastStems[i].stem === name) s = lastStems[i];
    if (!s) return Promise.reject(new Error('no stem ' + name));
    decodedStems[name] = fetch(s.url, { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('stem ' + name + ' ' + r.status); return r.arrayBuffer(); })
      .then(function (ab) {
        var sr = sniffRate(ab) || 44100;
        if (sr < 8000 || sr > 96000) sr = 44100;
        var OAC = window.OfflineAudioContext || window.webkitOfflineAudioContext;
        return new OAC(2, 1, sr).decodeAudioData(ab);
      });
    return decodedStems[name];
  }
  function buildMix(kind, btn) {
    var names = MIX_DEF[kind]; if (!names || !window.AudioPost) return;
    var label = btn.textContent; btn.classList.add('is-busy'); btn.textContent = T.mix_preparing || label;
    Promise.all(names.map(stemBuffer)).then(function (bufs) {
      var len = Math.max.apply(null, bufs.map(function (b) { return b.length; })), sr = bufs[0].sampleRate;
      var L = new Float32Array(len), R = new Float32Array(len), chans = [L, R];
      bufs.forEach(function (b) {
        for (var c = 0; c < 2; c++) { var d = chans[c], x = b.getChannelData(Math.min(c, b.numberOfChannels - 1)); for (var i = 0; i < x.length; i++) d[i] += x[i]; }
      });
      var peak = 0; for (var c2 = 0; c2 < 2; c2++) { var d2 = chans[c2]; for (var k = 0; k < len; k += 8) { var a = Math.abs(d2[k]); if (a > peak) peak = a; } }
      if (peak > 0.99) { var g = 0.99 / peak; for (var c3 = 0; c3 < 2; c3++) { var d3 = chans[c3]; for (var k3 = 0; k3 < len; k3++) d3[k3] *= g; } }
      var buf = window.AudioPost.makeBuf(2, len, sr, chans);
      var fmt = wantedFormat();
      var base = 'mix-' + (kind === 'rhythm' ? 'rhythm-section' : 'no-' + kind);
      if (fmt === 'mp3') save(encodeMp3(buf), base + '.mp3');
      else save(window.AudioPost.encodeWav(buf), base + '.wav');   // FLAC we cannot write, WAV is the lossless answer
      track('ready_mix', { kind: kind });
    }).catch(function () { toast(T.error || 'Error', 'error'); })
      .then(function () { btn.classList.remove('is-busy'); btn.textContent = label; });
  }

  function downloadZip() {
    if (!window.JSZip) { toast(T.error || 'ZIP unavailable', 'error'); return; }
    var fmt = wantedFormat();
    var label = els.zip.textContent;
    els.zip.classList.add('is-busy'); els.zip.disabled = true;
    if (fmt !== 'flac') els.zip.textContent = T.converting || label;
    var zip = new window.JSZip();
    Promise.all(lastStems.map(function (s) {
      var name = (T['stem_' + s.stem] || s.stem);
      if (fmt === 'flac') return fetchBlob(s.url).then(function (bl) { zip.file(name + '.' + (s.format || 'flac'), bl); });
      return convert(s, fmt).then(function (out) { zip.file(name + '.' + out.ext, out.blob); });
    })).then(function () { return zip.generateAsync({ type: 'blob' }); })
      .then(function (bl) { save(bl, 'stems.zip'); track('download_all', { fmt: fmt }); })
      .catch(function () { toast(T.error || 'Error', 'error'); })
      .then(function () { els.zip.classList.remove('is-busy'); els.zip.disabled = false; els.zip.textContent = label; });
  }

  // Bridge: fetch every stem, stash each under its own handoff key, then follow
  // the link. Fetch first so a stale signed URL fails HERE (toast) rather than as
  // an empty mixer. Files are named by stem so the mixer's track labels read
  // «vocals / drums / bass / other», not «track 1..4».
  var HANDOFF_KEY = '__handoff_stems_to_mixer_v1_';
  var HANDOFF_MAX = 8;
  function openInMixer(ev) {
    if (!lastStems.length || typeof window.__toolHandoffPut !== 'function') return; // plain link
    ev.preventDefault();
    var link = els.toMixer, href = link.getAttribute('href');
    link.classList.add('is-busy');
    var label = link.textContent;
    link.textContent = T.mixer_preparing || label;
    var stems = lastStems.slice(0, HANDOFF_MAX);
    Promise.all(stems.map(function (s, i) {
      return fetchBlob(s.url).then(function (bl) {
        var ext = s.format || 'flac';
        // Имя файла — ЧЕЛОВЕЧЕСКОЕ (оно станет подписью фейдера), а машинная
        // принадлежность едет параметром MIME. Отдельным ключом передачи её
        // возить нельзя: приёмник разбирает ключи параллельно, и манифест то
        // успевает, то нет — подсказка выходила случайной. Здесь идентичность
        // неотделима от самого файла, гоняться нечему.
        var mime = (bl.type || (ext === 'flac' ? 'audio/flac' : 'audio/mpeg')).split(';')[0];
        var f = new File([bl], (T['stem_' + s.stem] || s.stem) + '.' + ext,
                         { type: mime + ';stem=' + s.stem });
        return window.__toolHandoffPut(HANDOFF_KEY + i, f);
      });
    })).then(function (oks) {
      if (oks.some(function (o) { return !o; })) throw new Error('handoff put failed');
      // trailing count so the receiver knows how many keys to take, plus the
      // stem KEYS in the same order.
      //
      // Why the keys and not the file names: the files above are named with the
      // LOCALISED label («Барабаны.flac»), because that is what a person wants to
      // see on a fader. Matching on that name would work in Russian and silently
      // do nothing in the other eight locales — the same trap as any UI string
      // used as an identifier. The mixer needs the stable key to know that this
      // fader is drums, so it travels separately.
      return window.__toolHandoffPut(HANDOFF_KEY + 'count',
        new File([String(stems.length)], 'count.txt', { type: 'text/plain' }));
    }).then(function () {
      track('stems_to_mixer', { n: stems.length });
      window.location.href = href;
    }).catch(function () {
      link.classList.remove('is-busy'); link.textContent = label;
      // T.error is «Не удалось разделить трек» — the SPLIT-failed message. By the
      // time this runs the split has already succeeded and the stems are on
      // screen; only the carry to the mixer failed. Saying the split failed sends
      // the user back to re-buy work they already own (seen live: olga.belyaeva
      // 2026-08-16 20:43, split_ok at 20:41 then this toast on the mixer button).
      toast(T.handoff_failed || 'Could not open the stems in the mixer — the tracks are still here, download them or try again.', 'warning');
      track('stems_to_mixer_failed', { n: stems.length });
    });
  }

  function save(blob, name) {
    if (window.__downloadFile) { window.__downloadFile(blob, name); return; }
    var a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = name;
    a.click();
  }

  function setBusy(b, isPreview) {
    if (els.go) els.go.disabled = b || !selectedFile;
    if (els.previewGo) els.previewGo.disabled = b || !selectedFile;
    if (els.progress) els.progress.hidden = !b;
    if (!b && stageTimer) { clearTimeout(stageTimer); stageTimer = null; }
    if (b) {
      if (els.result) els.result.hidden = true;
      if (isPreview && els.previewBox) els.previewBox.hidden = true;
      if (els.stage) els.stage.textContent = (isPreview ? T.preview_running : T.stage_queued) || els.stage.textContent;
      if (els.eta) els.eta.textContent = T.keep_tab || '';
      // «Also saved to My generations for N days» is true of a paid split and
      // false of an excerpt, which is reaped in hours and never enters the library.
      if (els.stageLib) els.stageLib.hidden = !!isPreview;
    }
    // The wait, not the playback, is what must survive a dimming screen.
    if (window.__wakeLock) { b ? window.__wakeLock.request() : (window.__wakeLock.release && window.__wakeLock.release()); }
  }

  // The job DELIVERS first and charges second (see StemSplitJob), so for a moment
  // after `done` the wallet still shows the pre-split balance. Reading it once
  // right then displays a stale number and looks like «it was free». Second read
  // a beat later lands after the capture. Measured in the e2e, 2026-08-17.
  function refreshBalance() {
    refreshInfo();
    setTimeout(refreshInfo, 2500);
  }

  /** Re-read balance AND the remaining free excerpts — both change after a run. */
  function refreshInfo() {
    fetch(cfg.infoUrl, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (!d || !d.enabled) return;
        window.__stemInfo = d;
        renderBalance(d.balance);
        renderPreviewLeft(d.preview_remaining);
        renderFreeLane();
        syncLibraryLink(d);
      })
      .catch(function () {});
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
